<template>
  <div
    v-if="auctionStatus === AuctionStatuses.ENDED"
    class="lot-main-seller-block auction-status-block box-shadow"
  >
    <div class="auction-status-wrapper">
      <div class="title">{{ trans("live.auction_states.closed.title") }}</div>
      <button class="link-dashed">{{ trans("live.auction_states.closed.download_results") }}</button>
      <button class="link-dashed">{{ trans("live.auction_states.closed.download_log") }}</button>
    </div>
  </div>

  <div
    v-else
    :class="['lot-main-seller-block box-shadow', `status-${auctionStatus.toLowerCase()}`]"
  >
    <div v-if="activeLot?.id" class="lot-info-wrapper">
      <div class="lot-number">{{ trans("live.common.main_info.lot_number", { number: activeLot.lot_number }) }}</div>
      <div class="lot-meta" :class="{ single: isSingleMeta }">
        <span v-if="condition" class="meta">{{ condition }}</span>
        <span v-if="(catalogValue || vendorName) && condition" class="separator"></span>
        <span v-if="catalogValue" class="meta">{{ catalogValue }}</span>
        <span v-if="vendorName && catalogValue" class="separator"></span>
        <span v-if="vendorName" class="meta meta-vendor">{{ vendorName }}</span>
      </div>
      <div class="lot-image">
        <div class="mobile-vendor-name-wrapper">
          <div class="vendor-name">{{ vendorName }}</div>
        </div>
        <lot-gallery class="seller-gallery"></lot-gallery>
        <div v-if="activeLot?.admin_notes?.length" class="admin-note">{{ activeLot?.admin_notes }}</div>

        <div class="mobile-bid-info-wrapper">
          <div id="history-mobile-wrapper">
            <div class="lot-timeline-messages"></div>
          </div>
          <div v-if="activeLot?.admin_notes?.length" class="admin-note mobile-block">{{ activeLot?.admin_notes }}</div>
          <div class="current-lot-info-block">
            <div class="current-bid-info">
              <div v-if="activeLot.leaders_list?.length" class="bidder-name">
                {{ activeLot.leaders_list[0].user_name }}
              </div>
              <div class="bidder-bid">
                {{
                  activeLot.leaders_list?.length
                    ? format_currency(activeLot.leaders_list[0].amount)
                    : trans("live.common.leaders_list.no_bids")
                }}
              </div>
            </div>
            <div class="separator-line"></div>
            <div class="lot-meta" :class="{ single: isSingleMeta }">
              <span v-if="condition" class="meta">{{ condition }}</span>
              <span v-if="(catalogValue || catalog) && condition" class="separator"></span>
              <span v-if="catalogValue" class="meta">{{ catalogValue }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="mobile-actions-wrapper">
        <div class="mobile-actions-block">
          <div class="current-bid">{{ format_currency(currentBid) }}</div>
          <div class="actions-buttons-wrapper"></div>
          <fast-bid-variants-toggle target=".actions-buttons-wrapper .can-toggle-on-mob"></fast-bid-variants-toggle>
        </div>
      </div>

      <div class="lot-description-block">
        <custom-scrollbar class="custom-scrollbar mini lot-description-scrollbar controller-scrollbar">
          <div class="title">{{ activeLot.title }}</div>
          <div class="description" v-html="activeLot.description"></div>
        </custom-scrollbar>
      </div>
    </div>

    <template v-if="auctionStatus !== AuctionStatuses.ACTIVE">
      <div class="event-messages">
        <History></History>
      </div>
      <div :class="['right-block', { 'small-margin': auctionStore.liveSettings?.show_bid_increments }]">
        <template v-if="activeLot.leaders_list?.length">
          <div
            class="right-block__bid winner"
            @click="toggleShowActionButtons"
            :class="{ toggled: isShowActionButtons }">
            <div class="current-bid-anim">
              <transition name="current-bid">
                <div class="current-bid" :key="currentBid ?? 0">
                  {{ format_currency(currentBid) }}
                </div>
              </transition>
            </div>

          </div>
          <template v-if="!isShowChangePriceForm">
            <div v-if="auctionStore.liveSettings?.show_bid_increments" class="right-block__increment">
              {{ trans("live.common.leaders_list.increment", { amount: formattedIncrement }) }}
            </div>
            <div
              v-if="activeLot.leaders_list[0] && activeLot.leaders_list[0].amount > currentBid"
              class="right-block__max-bid">
              {{ format_currency(activeLot.leaders_list[0].amount) }}
            </div>
            <div v-if="isShowActionButtons" class="right-block-action-buttons">
              <span @click="toggleShowChangePriceForm" class="action-button stage-b-item">
                {{ trans("live.common.leaders_list.change_price") }}
              </span>
              <span @click="retractBid" class="action-button">
                {{ trans("live.common.leaders_list.retract_bid") }}
              </span>
            </div>
          </template>
          <div v-else class="change-price-form">
            <custom-input
              type="number"
              input-class="input"
              v-model="changePriceAmount"
              :placeholder="trans('live.common.leaders_list.change_price_placeholder')"></custom-input>
            <button class="btn btn-blue btn-click-effect" @click="changePrice">
              {{ trans("live.common.buttons.confirm") }}
            </button>
            <button class="btn btn-blue-outline btn-click-effect" @click="isShowChangePriceForm = false">
              {{ trans("live.common.buttons.cancel") }}
            </button>
          </div>
          <div class="separator"></div>
          <div :class="['bidder-current-name', { 'margin-bottom-0': isShowActionButtons }]">
            {{ activeLot.leaders_list[0].user_name }}
          </div>
          <div
            :class="['bidders-history', { 'hidden-action-buttons': isShowActionButtons }]"
            v-if="leadersListWithoutBookBid?.length">
            <custom-scrollbar
              v-if="!isShowActionButtons"
              class="custom-scrollbar controller-scrollbar bidders-history-scrollbar">
              <div
                :class="['bidder-name', { active: bidderStatus(bid) }]"
                v-for="(bid, index) in leadersListWithoutBookBid.slice(0, 4)"
                :key="index">
                {{ bid.user_name }}
              </div>
            </custom-scrollbar>
          </div>
        </template>
        <template v-else>
          <div class="right-block__bid no-bids">
            {{ format_currency(currentBid) }}
          </div>
          <div class="separator"></div>
          <div class="bidders-history">
            <div class="no-bids-label">
              {{ trans("live.common.leaders_list.no_bids") }}
            </div>
          </div>
        </template>
        <div class="separator"></div>
        <div v-if="liveBreakEnd" class="time-to-resume-wrapper">
          <div class="resume-label">{{ trans("live.common.leaders_list.break_enabled") }}</div>
          <!--        todo: Addon #259-->
          <!--        <timer-live-->
          <!--          :data-days="0"-->
          <!--          :data-hours="23"-->
          <!--          :data-minutes="15"-->
          <!--          :data-seconds="50"-->
          <!--        ></timer-live>-->
        </div>
        <template v-else>
          <Teleport :disabled="!isScreenL" to=".mobile-actions-wrapper .mobile-actions-block" defer>
            <TimeLine></TimeLine>
          </Teleport>
        </template>
      </div>

      <Teleport
        :disabled="!isScreenL"
        to=".mobile-actions-wrapper .mobile-actions-block .actions-buttons-wrapper"
        defer>
        <ActionButtons />
      </Teleport>
    </template>

    <div class="auction-status-block" v-else>
      <div class="auction-status-wrapper">
        <timer-live-auction-begin />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from "vue";
  import { History } from "@/components/action-block/tabs";
  import Api from "@/services/api";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { formatCatalogValueRange, humanizedCatalog, humanizedCondition } from "@/utils/lot";
  import { trans } from "@/utils/trans";
  import { format_currency } from "@/utils/currencies";
  import { AuctionStatuses } from "@/enum";
  import LotGallery from "@/components/_partials/LotGallery.vue";
  import CustomScrollbar from "@/components/_partials/CustomScrollbar.vue";
  import ActionButtons from "@/components/seller/ActionButtons.vue";
  import TimeLine from "@/components/_partials/TimeLine.vue";
  import CustomInput from "@/components/_partials/widget/CustomInput.vue";
  import { isObject } from "lodash";
  import { useToast } from "primevue/usetoast";
  import FastBidVariantsToggle from "@/components/_partials/FastBidVariantsToggle.vue";
  import { isScreenL } from "@/utils/media-size";
  import TimerLiveAuctionBegin from "@/components/_partials/TimerLiveAuctionBegin.vue";

  const auctionStore = useAuctionStore();
  const toast = useToast();
  const auctionStatus = computed(() => auctionStore.auction.status);

  const bidIncrementRules = computed(() => auctionStore.auction.bid_increment_rules);
  const auction = computed(() => auctionStore.auction);
  const activeLot = computed(() => (auctionStore.activeLot?.id ? auctionStore.activeLot : auctionStore.fakeActiveLot));
  const currentBid = computed(() => (activeLot.value ? activeLot.value?.current_bid : 0));
  const catalog = computed(() => (activeLot.value ? humanizedCatalog(activeLot.value, true) : null));
  const catalogValue = computed(() => (activeLot.value ? formatCatalogValueRange(activeLot.value, true) : null));
  const vendorName = computed(() => (activeLot.value ? activeLot.value?.vendor_name : null));
  const condition = computed(() => (activeLot.value ? humanizedCondition(activeLot.value, true) : null));
  const isSingleMeta = computed(() => {
    const result = [];
    catalog.value && result.push(catalog);
    catalogValue.value && result.push(catalogValue);
    condition.value && result.push(condition);
    return result.length === 1;
  });
  const formattedIncrement = computed(() => {
    const rule = bidIncrementRules.value
      .slice()
      .reverse()
      .find((rule) => currentBid.value && currentBid.value >= rule.min_current_bid);

    return format_currency(rule ? rule.bid_step : 1);
  });
  const formattedCatalog = (catalog: string) => {
    return catalog.length > 5 ? catalog.slice(0, 5) + "..." : catalog;
  };
  const isShowActionButtons = ref(false);
  const isShowChangePriceForm = ref(false);
  const changePriceAmount = ref<number>(null);

  const liveBreakEnd = computed(() => auction.value.live_break_end);
  const leadersListWithoutBookBid = computed(() => {
    if (!activeLot.value?.leaders_list?.length) {
      return [];
    }

    const firstBidder = activeLot.value?.leaders_list[0];

    return activeLot.value?.leaders_list?.filter((item) => {
      return !item.is_book_bid && item.user_id !== firstBidder.user_id;
    });
  });

  const retractBid = () => {
    if (activeLot.value?.id) {
      Api.post("/live/lots/" + activeLot.value.id + "/retract-bid").catch((error) => {
        console.log(error);
      });
    }
  };

  const toggleShowActionButtons = () => {
    isShowActionButtons.value = !isShowActionButtons.value;
  };

  const toggleShowChangePriceForm = () => {
    isShowChangePriceForm.value = !isShowChangePriceForm.value;
  };

  const changePrice = () => {
    Api.post("/api/lots/" + activeLot.value.id + "/update-current-bid", {
      amount: changePriceAmount.value
    })
      .then((response) => {
        if (response.data.success) {
          toast.add({
            severity: "success",
            summary: trans("live.toast.success"),
            detail: trans("live.toast.lot.price_changed"),
            life: 6000
          });
          changePriceAmount.value = null;
          isShowChangePriceForm.value = false;
        }
      })
      .catch((error) => {
        if (isObject(error.response.data?.errors)) {
          const errors = [];
          Object.values(error.response.data.errors).forEach((itemErrors) => {
            errors.push(...itemErrors);
          });

          errors.forEach((error) => {
            toast.add({
              severity: "error",
              summary: trans("live.toast.error"),
              detail: error || trans("live.toast.unknown_error"),
              life: 6000
            });
          });
        }
      });
  };

  const bidderStatus = (bidInfo) => {
    const userData = auctionStore.userData[bidInfo?.user_id];
    return userData && userData.status === "active";
  };
</script>
